import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Search, AlertCircle } from 'lucide-react';
import { useCurrentUser } from '../../hooks/useAuth';
import { formatDateTime } from '../../lib/dateFmt';
import { SkeletonList } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { api, ApiError } from '../../lib/api';

interface Row {
  id: string;
  recipient: string;
  subject: string;
  template_key: string | null;
  message_id: string | null;
  status: 'sent' | 'failed';
  error: string | null;
  person_id: string | null;
  via: string | null;
  sent_at: string;
}

const ALLOWED = ['admin', 'super_admin', 'coordinator'];

/**
 * Read-only viewer for the email_log table. Coordinators triage
 * "I never got my certificate" reports here; the message_id column
 * cross-references DuoCircle's relay portal.
 */
export function EmailLog() {
  const { data: user, isLoading } = useCurrentUser();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recipient, setRecipient] = useState('');
  const [status, setStatus] = useState<'' | 'sent' | 'failed'>('');
  const [templateKey, setTemplateKey] = useState('');

  useEffect(() => {
    if (!user || !user.roles.some((r) => ALLOWED.includes(r))) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (recipient) params.set('recipient', recipient);
        if (status) params.set('status', status);
        if (templateKey) params.set('template_key', templateKey);
        const r = await api<{ rows: Row[] }>(
          `/api/admin/email-log${params.size > 0 ? `?${params.toString()}` : ''}`,
        );
        if (!cancelled) setRows(r.rows);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'load failed');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, recipient, status, templateKey]);

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Please <Link to="/login" className="underline">sign in</Link>.
      </main>
    );
  }
  if (!user.roles.some((r) => ALLOWED.includes(r))) {
    return (
      <main className="p-6 text-sm text-fg-muted">
        Email log is restricted to admin / coordinator.
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-info-soft/15 text-info-soft-fg">
            <Mail className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-info-soft-fg">
              Operational
            </div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Email log
            </h1>
            <p className="mt-1 text-sm text-fg-muted">
              Every transactional email the system tried to send: certificates,
              PPE questionnaires, feedback surveys, audits. Cross-reference the
              message-id column with the DuoCircle portal when you need to
              prove delivery.
            </p>
          </div>
        </header>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Recipient</span>
            <div className="relative mt-1.5">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim" />
              <input
                type="search"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="filter by email"
                className="block h-10 w-full rounded-md border border-line-strong/20 bg-surface pl-9 pr-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
              />
            </div>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as '' | 'sent' | 'failed')}
              className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            >
              <option value="">all</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Template</span>
            <select
              value={templateKey}
              onChange={(e) => setTemplateKey(e.target.value)}
              className="mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
            >
              <option value="">all</option>
              <option value="certificate">certificate</option>
              <option value="feedback_survey">feedback_survey</option>
            </select>
          </label>
        </div>

        {error ? (
          <div className="mt-4 flex items-start gap-2 rounded-md bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <div className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          {rows === null ? (
            <SkeletonList rows={6} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Mail className="h-6 w-6" />}
              title="Nothing logged"
              description="Nothing matches this filter."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-surface-2 text-xs uppercase tracking-wider text-fg-muted">
                  <tr>
                    <th className="px-4 py-2.5">When</th>
                    <th className="px-4 py-2.5">Recipient</th>
                    <th className="px-4 py-2.5">Subject</th>
                    <th className="px-4 py-2.5">Template</th>
                    <th className="px-4 py-2.5">Via</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Message-Id / Error</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-line/10 transition-colors hover:bg-surface-2/50"
                    >
                      <td className="px-4 py-2.5 align-top text-xs tabular-nums text-fg-muted">
                        {formatDateTime(r.sent_at)}
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs">
                        {r.person_id ? (
                          <Link
                            to={`/admin/people/${r.person_id}`}
                            className="text-info-soft-fg hover:underline"
                          >
                            {r.recipient}
                          </Link>
                        ) : (
                          r.recipient
                        )}
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs">
                        {r.subject}
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs">
                        {r.template_key ? (
                          <span className="rounded bg-surface-2 px-2 py-0.5 font-mono text-[10px] font-semibold text-fg-muted">
                            {r.template_key}
                          </span>
                        ) : (
                          <span className="text-fg-dim">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs text-fg-muted">
                        {r.via ?? '—'}
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs">
                        <span
                          className={`inline-flex items-center rounded px-2 py-0.5 font-mono ${
                            r.status === 'failed'
                              ? 'bg-danger-soft/15 text-danger-soft-fg'
                              : 'bg-success-soft/15 text-success-soft-fg'
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 align-top text-xs">
                        {r.status === 'failed' ? (
                          <span className="text-danger-soft-fg">{r.error ?? 'unknown error'}</span>
                        ) : (
                          <code className="break-all font-mono text-fg-muted">
                            {r.message_id ?? '—'}
                          </code>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
